/**
 * Elegir un país escribiendo, en vez de bajar doscientos.
 *
 * EL PROBLEMA CON UN `<select>` DE DOSCIENTOS PAÍSES: el sistema abre una
 * lista que ocupa la pantalla entera, empieza en Afganistán, y para llegar a
 * Costa Rica hay que arrastrar. En un teléfono es lo peor de todo: la lista
 * tapa el formulario, no respeta el tema, y un resbalón del dedo elige Benín.
 *
 * Acá se escribe «cos» y queda Costa Rica. Tres letras contra tres segundos
 * de arrastre.
 *
 * ES UN COMBOBOX DE VERDAD, con los roles ARIA que corresponden
 * (`combobox` + `listbox` + `option`, `aria-activedescendant`), y responde a
 * las teclas que la gente ya usa sin que nadie se lo enseñe: flechas para
 * moverse, Enter para elegir, Escape para cerrar. Sin eso sería una caja de
 * texto bonita que quien no usa mouse no puede completar.
 *
 * COSTA RICA VA PRIMERA, y no es capricho: es una app costarricense y va a
 * ser la respuesta de la enorme mayoría. El resto va en orden alfabético del
 * idioma en que se esté mirando.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CountryOption {
  code: string;
  /** El nombre ya traducido al idioma actual. */
  name: string;
  flag: string;
  /** El prefijo telefónico, cuando se usa para eso. */
  dialCode?: string;
}

interface CountryPickerProps {
  id: string;
  label: string;
  help?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  /** El código elegido, o `''` si todavía no eligió. */
  value: string;
  options: readonly CountryOption[];
  onChange: (code: string) => void;
  disabled?: boolean;
  /** Códigos que van arriba del todo. Por defecto, Costa Rica. */
  pinned?: readonly string[];
}

/** Minúsculas y sin tildes, para que «peru» encuentre «Perú». */
export function foldForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Los países que coinciden con lo escrito.
 *
 * Los que EMPIEZAN con lo tecleado van primero. Escribiendo «ind», «India»
 * tiene que salir antes que «Islas Turcas», que también contiene «ind» en
 * otra parte; sin ese orden, lo que se busca aparece en la mitad de la lista.
 */
export function filterCountries(
  options: readonly CountryOption[],
  query: string,
  // Costa Rica primero por defecto, y la decisión vive ACÁ y no en quien
  // llama: si el valor por defecto fuera «ninguno», cada pantalla tendría que
  // acordarse de pasarlo y la que se olvide arranca en Afganistán.
  pinned: readonly string[] = ['CR'],
): CountryOption[] {
  const q = foldForSearch(query);

  if (q === '') {
    const arriba = pinned
      .map((code) => options.find((o) => o.code === code))
      .filter((o): o is CountryOption => o !== undefined);
    const resto = options.filter((o) => !pinned.includes(o.code));
    return [...arriba, ...resto];
  }

  const empiezan: CountryOption[] = [];
  const contienen: CountryOption[] = [];
  for (const option of options) {
    const nombre = foldForSearch(option.name);
    // El código también busca: quien escribe «CR» sabe lo que quiere.
    if (nombre.startsWith(q) || foldForSearch(option.code) === q) empiezan.push(option);
    else if (nombre.includes(q) || (option.dialCode ?? '').startsWith(q.replace(/\D/g, ''))) {
      if (q.replace(/\D/g, '') !== '' || nombre.includes(q)) contienen.push(option);
    }
  }
  return [...empiezan, ...contienen];
}

export function CountryPicker({
  id,
  label,
  help,
  error,
  hint,
  value,
  options,
  onChange,
  disabled = false,
  pinned = ['CR'],
}: CountryPickerProps): JSX.Element {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /**
   * Qué opción está resaltada. `-1` = ninguna, y es como arranca.
   *
   * NO arranca en 0 a propósito. Si al abrirse quedara resaltada la primera,
   * alguien que enfoca el campo y aprieta Enter para enviar el formulario
   * elegiría Costa Rica sin haberlo pedido. Con `-1`, Enter no hace nada
   * hasta que la persona baje con la flecha.
   */
  const [active, setActive] = useState(-1);
  const caja = useRef<HTMLDivElement>(null);

  const elegido = options.find((o) => o.code === value) ?? null;
  const visibles = useMemo(
    () => filterCountries(options, open ? query : '', pinned).slice(0, 60),
    [options, query, open, pinned],
  );

  // Un clic afuera cierra. Sin esto la lista se queda abierta tapando el
  // formulario mientras la persona intenta escribir en otro campo.
  useEffect(() => {
    if (!open) return;
    const alClic = (event: MouseEvent): void => {
      if (caja.current !== null && !caja.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', alClic);
    return () => document.removeEventListener('mousedown', alClic);
  }, [open]);

  const helpId = help === undefined ? undefined : `${id}-help`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [helpId, errorId].filter((x): x is string => x !== undefined).join(' ');

  function elegir(option: CountryOption): void {
    onChange(option.code);
    setQuery('');
    setOpen(false);
  }

  function alTeclado(event: React.KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => Math.min(Math.max(i + delta, 0), visibles.length - 1));
      return;
    }
    if (event.key === 'Enter' && open && active >= 0) {
      const option = visibles[active];
      if (option !== undefined) {
        event.preventDefault();
        elegir(option);
      }
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className={error === undefined ? 'field combo' : 'field combo invalid'} ref={caja}>
      <label htmlFor={id}>
        {label}
        {hint !== undefined && <span className="help"> ({hint})</span>}
      </label>
      {help !== undefined && (
        <span className="help" id={helpId}>
          {help}
        </span>
      )}

      <div className="combo-field">
        {/* La bandera es decorativa: el nombre del país ya está escrito al
            lado, y media docena de tricolores se parecen entre sí. */}
        <span className="combo-flag" aria-hidden="true">
          {elegido?.flag ?? '🌐'}
        </span>
        <input
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={describedBy === '' ? undefined : describedBy}
          aria-invalid={error === undefined ? undefined : true}
          aria-activedescendant={
            open && active >= 0 && visibles[active] ? `${listId}-${visibles[active]!.code}` : undefined
          }
          disabled={disabled}
          placeholder={t('country.placeholder')}
          // Cuando está cerrado se muestra el país elegido; al abrirse, lo
          // que se va escribiendo. Si mostrara siempre lo tecleado, cerrar
          // sin elegir dejaría el campo con un texto a medias que no
          // corresponde a lo guardado.
          value={open ? query : (elegido?.name ?? '')}
          onFocus={() => {
            setOpen(true);
            setQuery('');
            setActive(-1);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={alTeclado}
        />
        {elegido !== null && !disabled && (
          <button
            type="button"
            className="combo-clear"
            aria-label={t('country.clear')}
            onClick={() => {
              onChange('');
              setQuery('');
              setOpen(true);
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>

      {open && (
        <ul className="combo-list" id={listId} role="listbox" aria-label={label}>
          {visibles.length === 0 ? (
            <li className="combo-empty">{t('country.none')}</li>
          ) : (
            visibles.map((option, index) => (
              <li key={option.code} role="none">
                <button
                  type="button"
                  id={`${listId}-${option.code}`}
                  role="option"
                  aria-selected={option.code === value}
                  className={index === active ? 'combo-option active' : 'combo-option'}
                  // `onMouseDown` y no `onClick`: el clic llega después del
                  // `blur` del campo, y para entonces la lista ya se cerró.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    elegir(option);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  <span aria-hidden="true">{option.flag}</span>
                  <span>{option.name}</span>
                  {option.dialCode !== undefined && (
                    <span className="help">+{option.dialCode}</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      {error !== undefined && (
        <span className="error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
